import { ref, type MaybeRefOrGetter, toValue, readonly } from 'vue';

import { appStorageController, ProjectSettings, type AppStorageController } from '@/shared/lib';

enum ResizeSide {
  LEFT = 'left',
  RIGHT = 'right',
}

const useResizableWithStorage = (
  storageKey: MaybeRefOrGetter<string>,
  resizeSide: MaybeRefOrGetter<ResizeSide>,
  storageController: AppStorageController = appStorageController,
) => {
  const DEFAULT_WIDTH = ProjectSettings.shared.resizablePabel.width.default;
  const MIN_WIDTH = ProjectSettings.shared.resizablePabel.width.min;
  const MAX_WIDTH = ProjectSettings.shared.resizablePabel.width.max;

  const width = ref<number>(DEFAULT_WIDTH);

  const loadWithFromStorage = (): void => {
    const savedWidth: number | null = storageController.getPanelWidth(toValue(storageKey));

    if (savedWidth !== null) {
      width.value = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, savedWidth));
    }
  };

  const onStartResize = (event: MouseEvent): void => {
    event.preventDefault();

    const startX = event.clientX;
    const startWidth = width.value;

    const doResize = (moveEvent: MouseEvent): void => {
      const deltaX =
        toValue(resizeSide) === ResizeSide.LEFT
          ? startX - moveEvent.clientX
          : moveEvent.clientX - startX;
      const newWidth = startWidth + deltaX;

      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        width.value = newWidth;
      }
    };

    const stopResize = (): void => {
      storageController.setPanelWidth(toValue(storageKey), width.value);
      window.removeEventListener('mousemove', doResize);
      window.removeEventListener('mouseup', stopResize);
    };

    window.addEventListener('mousemove', doResize);
    window.addEventListener('mouseup', stopResize);
  };

  return { width: readonly(width), loadWithFromStorage, onStartResize };
};

export { ResizeSide, useResizableWithStorage };
